import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, Zap, ExternalLink, BookOpen, User, Calendar, Eye, X, Lock, AlertCircle } from 'lucide-react';
import { PlayCircleOutlined, FileTextOutlined } from '@ant-design/icons';

const TaskCard = ({ task, index, isPreview = false, isOverview = false, canAccessExpiredTasks = false, canAccessAllTasks = false }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    if (showDetailsModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showDetailsModal]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Completed':
        return { bg: 'bg-secondary/10', text: 'text-secondary-dark', border: 'border-secondary/30', dot: 'bg-secondary', icon: CheckCircle };
      case 'In Progress':
        return { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/30', dot: 'bg-primary', icon: Zap };
      case 'Pending':
        return { bg: 'bg-neutral-100', text: 'text-neutral-500', border: 'border-neutral-200', dot: 'bg-neutral-400', icon: Clock };
      case 'Locked':
        return { bg: 'bg-neutral-100', text: 'text-neutral-400', border: 'border-neutral-200', dot: 'bg-neutral-300', icon: Lock };
      case 'Expired':
        return { bg: 'bg-neutral-100', text: 'text-neutral-500', border: 'border-neutral-200', dot: 'bg-neutral-400', icon: AlertCircle };
      default:
        return { bg: 'bg-neutral-100', text: 'text-neutral-500', border: 'border-neutral-200', dot: 'bg-neutral-400', icon: Clock };
    }
  };

  const statusConfig = getStatusConfig(task.status);
  const StatusIcon = statusConfig.icon;
  const displayNumber = typeof task.displayNumber === 'number' ? task.displayNumber : (task.id || index + 1);

  const statusClassName = `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`;
  const dotClassName = `w-2 h-2 rounded-full ${statusConfig.dot}`;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        className="bg-white rounded-lg hover:shadow-card transition-all duration-200"
      >
        {/* Preview Banner */}
        {isPreview && (
          <div className="bg-secondary/10 border-b border-secondary/20 px-4 py-2 rounded-t-lg">
            <p className="text-xs font-medium text-secondary-dark text-center">
              Program Preview - Starts July 6, 2026
            </p>
          </div>
        )}

        <div className="p-3 sm:p-4 md:p-5">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-2 sm:gap-3 md:gap-4 mb-2 sm:mb-3">
            {/* Day Badge & Task Type */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-primary text-white rounded-lg font-semibold text-xs sm:text-sm flex-shrink-0">
                {displayNumber}
              </div>
              <div className="min-w-0">
                <span className="inline-block px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs font-medium rounded truncate">
                  {task.taskType}
                </span>
              </div>
            </div>
            
            {/* Status Badge */}
            <div className={statusClassName}>
              <StatusIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
              <span className="hidden sm:inline">{task.status}</span>
            </div>
          </div>

          {/* Task Title */}
          <h3 className="text-xs sm:text-sm md:text-base font-medium text-neutral-900 mb-2 sm:mb-3 leading-relaxed">
            {(!canAccessAllTasks && (task.status === 'Pending' || task.status === 'Locked')) ? (
              <span className="flex items-center gap-2 text-neutral-500">
                <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                Task will be revealed on the scheduled date
              </span>
            ) : (!canAccessAllTasks && !canAccessExpiredTasks && task.status === 'Expired') ? (
              <span className="text-neutral-400">This task has expired</span>
            ) : (
              task.task
            )}
          </h3>

          {/* Meta Info & Action */}
          {!(!canAccessAllTasks && (
            task.status === 'Pending' ||
            task.status === 'Locked' ||
            (task.status === 'Expired' && !canAccessExpiredTasks)
          )) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 pt-2 sm:pt-3 border-t border-neutral-100">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-neutral-500">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="truncate">{new Date(task.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </div>
                <div className="hidden sm:flex items-center gap-1">
                  <User className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="truncate">{task.assignedBy}</span>
                </div>
                <div className="flex items-center gap-1">
                  <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span>{task.resources.split(', ').length} Resources</span>
                </div>
              </div>
              
              <button
                onClick={() => setShowDetailsModal(true)}
                className="inline-flex items-center justify-center gap-1 px-2.5 sm:px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-md hover:bg-primary/90 transition-colors duration-200 whitespace-nowrap"
              >
                <Eye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="hidden sm:inline">View Details</span>
                <span className="sm:hidden">Details</span>
              </button>
            </div>
          )}

          {/* Locked/Expired State */}
          {!canAccessAllTasks && (task.status === 'Pending' || task.status === 'Locked' || (task.status === 'Expired' && !canAccessExpiredTasks)) && (
            <div className="flex items-center gap-2 pt-3 border-t border-neutral-100 text-xs text-neutral-500">
              <Lock className="w-3.5 h-3.5" />
              <span>{task.status === 'Expired' ? 'Task has expired' : `Unlocks on ${new Date(task.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetailsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetailsModal(false)}
            className="fixed inset-0 bg-primary/40 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 modal-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-md sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="bg-primary px-4 sm:px-6 py-4 sm:py-5">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center font-bold text-base sm:text-lg text-white flex-shrink-0">
                      {displayNumber}
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-xl font-bold text-white truncate">Day {displayNumber} Task</h2>
                      <p className="text-white/80 text-xs sm:text-sm truncate">
                        {new Date(task.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white flex-shrink-0"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4 sm:space-y-5">
                {/* Task Overview */}
                <div className="bg-primary/5 rounded-xl p-3 sm:p-5 border border-primary/10">
                  <h3 className="text-xs sm:text-sm font-bold text-primary mb-2 sm:mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 flex-shrink-0" />
                    Task Overview
                  </h3>
                  <p className="text-xs sm:text-sm text-neutral-700 leading-relaxed">{task.task}</p>
                </div>

                {/* Learning Topics */}
                <div className="bg-secondary/10 rounded-xl p-3 sm:p-5 border border-secondary/20">
                  <h3 className="text-xs sm:text-sm font-bold text-secondary-dark mb-2 sm:mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 flex-shrink-0" />
                    Learning Topics
                  </h3>
                  <p className="text-xs sm:text-sm text-neutral-700 leading-relaxed">{task.topics}</p>
                </div>

                {/* Resources */}
                <div className="bg-neutral-50 rounded-xl p-3 sm:p-5 border border-neutral-200">
                  <h3 className="text-xs sm:text-sm font-bold text-neutral-800 mb-3 sm:mb-4 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
                    Learning Resources
                  </h3>
                  <div className="space-y-3 sm:space-y-4">
                    {task.resources === 'Resources to be added' ? (
                      <div className="p-2 sm:p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                        <p className="text-xs sm:text-sm text-secondary-dark">
                          <span className="font-semibold">⏳ Resources Coming Soon</span> - Learning materials will be added shortly.
                        </p>
                      </div>
                    ) : task.resources.includes('Video Links:') ? (
                      <>
                        {/* Video Links Section */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide flex items-center gap-2">
                            <PlayCircleOutlined className="text-lg" /> Video Links
                          </p>
                          <div className="space-y-2">
                            {task.resources
                              .split('Video Links:')[1]
                              ?.split('Website Links:')[0]
                              ?.split('\n')
                              .filter(line => line.trim().startsWith('•') && line.includes('http'))
                              .map((line, idx) => {
                                const url = line.replace(/^[•\-*]\s*/, '').trim();
                                if (url.startsWith('http')) {
                                  return (
                                    <a
                                      key={`video-${idx}`}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white border border-neutral-200 rounded-lg text-xs sm:text-sm text-primary hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm transition-all duration-200"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                      <span className="font-medium truncate">Video Link {idx + 1}</span>
                                    </a>
                                  );
                                }
                                return null;
                              })}
                          </div>
                        </div>

                        {/* Website Links Section */}
                        {task.resources.includes('Website Links:') && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide flex items-center gap-2">
                              <FileTextOutlined className="text-lg" /> Website Links
                            </p>
                            <div className="space-y-2">
                              {task.resources
                                .split('Website Links:')[1]
                                ?.split('\n')
                                .filter(line => line.trim().startsWith('•') && line.includes('http'))
                                .map((line, idx) => {
                                  const url = line.replace(/^[•\-*]\s*/, '').trim();
                                  if (url.startsWith('http')) {
                                    return (
                                      <a
                                        key={`website-${idx}`}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white border border-neutral-200 rounded-lg text-xs sm:text-sm text-primary hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm transition-all duration-200"
                                      >
                                        <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                        <span className="font-medium truncate">Article {idx + 1}</span>
                                      </a>
                                    );
                                  }
                                  return null;
                                })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Fallback for old format */
                      task.resources.split(', ').map((resource, idx) => {
                        const parts = resource.trim().split(' - ');
                        let title = resource;
                        let url = null;
                        
                        if (parts.length >= 2) {
                          title = parts[0];
                          const urlPart = parts.find(part => part.startsWith('http'));
                          if (urlPart) url = urlPart;
                        }
                        
                        if (!url && resource.startsWith('http')) {
                          url = resource;
                          title = 'Resource Link';
                        }
                        
                        if (!url) {
                          const searchQuery = encodeURIComponent(title + ' tutorial');
                          url = `https://www.google.com/search?q=${searchQuery}`;
                        }
                        
                        return (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-white border border-neutral-200 rounded-lg text-sm text-primary hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm transition-all duration-200"
                          >
                            <ExternalLink className="w-4 h-4 flex-shrink-0" />
                            <span className="font-medium truncate">{title}</span>
                          </a>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Task Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-neutral-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span className="text-xs font-medium text-neutral-500">Date</span>
                    </div>
                    <p className="text-sm font-semibold text-neutral-900">
                      {new Date(task.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-neutral-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-primary" />
                      <span className="text-xs font-medium text-neutral-500">Assigned By</span>
                    </div>
                    <p className="text-sm font-semibold text-neutral-900">{task.assignedBy}</p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-neutral-50 px-6 py-4 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className={dotClassName} />
                  <span className="text-sm font-medium text-neutral-600">Status: {task.status}</span>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl transition-colors shadow-lg shadow-primary/20"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TaskCard;
